import type { PrefetchContext, PrefetchedDataProps } from 'src/shared/prefetch';

import type { MergedState } from 'src/app/shared/hooks/state';
import { useMergedStates } from 'src/app/shared/hooks/state';

import { prefetchTopicPageDataFromApi, useTopicPageDataFromApi } from './pageData';
import { prefetchTopicPageFcm, useAsyncTopicPageFcm } from './pageFcm';
import { prefetchTopicPageFcmExperiments, useAsyncTopicPageFcmExperiments } from './pageFcmExperiments';
import {
	prefetchTopicPagePaginatedPetitionUpdatesDataFromApi,
	useTopicPagePaginatedPetitionUpdatesDataFromApi,
} from './pagePetitionUpdatesPaginatedData';
import {
	prefetchTopicPagePaginatedRecentPetitionsDataFromApi,
	useTopicPagePaginatedRecentPetitionsDataFromApi,
} from './pageRecentPetitionsPaginatedData';
import { prefetchTopicSupporterVoicesDataFromApi, useTopicSupporterVoicesDataFromApi } from './pageSupporterVoiceData';
import {
	prefetchTopicPagePaginatedTrendingPetitionsDataFromApi,
	useTopicPagePaginatedTrendingPetitionsDataFromApi,
} from './pageTrendingPetitionsPaginatedData';
import {
	prefetchTopicPagePaginatedVictoriousPetitionsDataFromApi,
	useTopicPagePaginatedVictoriousPetitionsDataFromApi,
} from './pageVictoriousPetitionsPaginatedData';

export type TopicPagePrefetchedContext = Readonly<{
	pageData: Parameters<typeof useTopicPageDataFromApi>[0];
	petitionUpdatesPaginatedData: Parameters<typeof useTopicPagePaginatedPetitionUpdatesDataFromApi>[0];
	recentPetitionsPaginatedData: Parameters<typeof useTopicPagePaginatedRecentPetitionsDataFromApi>[0];
	trendingPetitionsPaginatedData: Parameters<typeof useTopicPagePaginatedTrendingPetitionsDataFromApi>[0];
	victoriousPetitionsPaginatedData: Parameters<typeof useTopicPagePaginatedVictoriousPetitionsDataFromApi>[0];
	supporterVoicesData: Parameters<typeof useTopicSupporterVoicesDataFromApi>[0];
}>;

export type TopicPageCombinedState = MergedState<
	ReturnType<typeof useTopicPageDataFromApi>,
	ReturnType<typeof useTopicPagePaginatedPetitionUpdatesDataFromApi>,
	ReturnType<typeof useTopicPagePaginatedRecentPetitionsDataFromApi>,
	ReturnType<typeof useTopicPagePaginatedTrendingPetitionsDataFromApi>,
	ReturnType<typeof useTopicPagePaginatedVictoriousPetitionsDataFromApi>,
	ReturnType<typeof useTopicSupporterVoicesDataFromApi>,
	ReturnType<typeof useAsyncTopicPageFcmExperiments>,
	ReturnType<typeof useAsyncTopicPageFcm>
>;

export function usePageContext(slug: string, prefetchedData?: TopicPagePrefetchedContext): TopicPageCombinedState {
	return useMergedStates(
		useTopicPageDataFromApi(prefetchedData?.pageData, slug),
		useTopicPagePaginatedPetitionUpdatesDataFromApi(prefetchedData?.petitionUpdatesPaginatedData, slug),
		useTopicPagePaginatedRecentPetitionsDataFromApi(prefetchedData?.recentPetitionsPaginatedData, slug),
		useTopicPagePaginatedTrendingPetitionsDataFromApi(prefetchedData?.trendingPetitionsPaginatedData, slug),
		useTopicPagePaginatedVictoriousPetitionsDataFromApi(prefetchedData?.victoriousPetitionsPaginatedData, slug),
		useTopicSupporterVoicesDataFromApi(prefetchedData?.supporterVoicesData, slug),
		useAsyncTopicPageFcmExperiments(),
		useAsyncTopicPageFcm(),
	);
}

export async function prefetchPageContext(slug: string, context: PrefetchContext): Promise<TopicPagePrefetchedContext> {
	const [
		pageData,
		petitionUpdatesPaginatedData,
		recentPetitionsPaginatedData,
		trendingPetitionsPaginatedData,
		victoriousPetitionsPaginatedData,
		supporterVoicesData,
	] = await Promise.all([
		prefetchTopicPageDataFromApi(context, slug),
		prefetchTopicPagePaginatedPetitionUpdatesDataFromApi(context, slug),
		prefetchTopicPagePaginatedRecentPetitionsDataFromApi(context, slug),
		prefetchTopicPagePaginatedTrendingPetitionsDataFromApi(context, slug),
		prefetchTopicPagePaginatedVictoriousPetitionsDataFromApi(context, slug),
		prefetchTopicSupporterVoicesDataFromApi(context, slug),
		prefetchTopicPageFcmExperiments(context),
		prefetchTopicPageFcm(context),
	]);

	return {
		pageData,
		petitionUpdatesPaginatedData,
		recentPetitionsPaginatedData,
		trendingPetitionsPaginatedData,
		victoriousPetitionsPaginatedData,
		supporterVoicesData,
	};
}

export type TopicPagePrefetchedDataProps = PrefetchedDataProps<TopicPagePrefetchedContext>;
